import React, { useEffect, useState, useLayoutEffect } from "react";

//@packages
import i18next from "i18next";
import { useSelector } from "react-redux";
import { ContentLoader } from "@forge/common";
import { addMessageListener, IframeComponent, sendMessage } from '@hcecbp/uiintegrationcomponent';

//@Utils
import authService, { getMCDEnabled } from "../../../authService";

//@Interfaces
import { IOrganisationState } from "../../commonUtils/Redux/Global/globalShared";

//@Styles
import './index.scss';

//@Functional Component
const GatewayManagement = () => {
    // tslint:disable-next-line:no-any
    const environmentVariables = (window as any).ENV;

    //Get Environment Variables
    const AUTHORITY = environmentVariables.AUTHORITY;
    const GATEWAY_MANAGEMENT_HOST_URL = environmentVariables.GATEWAY_MANAGEMENT_HOST_UI_URL;
    const hstName = window.location.href;
    const selectedLoginOption = "selectedLoginOption";
    //Get Client ID based on MCDSSO
    const getClientID = () => {
        //Check if MCDSSO is enabled
        const isMCDSSO = window.location.pathname.includes('mcd') || getMCDEnabled(window.location.search) || environmentVariables.MCD_INSTANCE || ((window as any).ENV.ADMIN_MCD_HOSTNAME !== '' ? hstName.includes((window as any).ENV.ADMIN_MCD_HOSTNAME) : false);

        //Return Client ID based on MCDSSO
        if (isMCDSSO) {
            //Check if MCD Instance is available
            return `${AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID_MCD}`;
        }
        else if (localStorage.getItem(selectedLoginOption) === 'SSO') {
            return `${environmentVariables.AUTHORITY_HON_SSO}:${environmentVariables.AUTHORITY_CLIENT_ID_HON_SSO}`;
        }
        else {
            //Return Client ID
            return `${AUTHORITY}:${environmentVariables.AUTHORITY_CLIENT_ID}`;
        }
    };


    //Get Window Inner Height
    const getWindowInnerHeight = () => window.innerHeight;
    //Calculate Content Inner Height for Iframe wrapper
    const getContentHeight = (inputInnerHeight?: number) => (inputInnerHeight || getWindowInnerHeight()) - 50;

    //State Variables
    const [isIFrameLoaded, setIsIFrameLoaded] = useState<boolean>(false); //State to check if IFrame is loaded
    const [iframeHeight, setIframeHeight] = useState<number>(getContentHeight()); //State to set IFrame Height

    //SI Org Selected from Redux Store
    const orgSelected = useSelector((state: IOrganisationState) => state.globalShared.gatewaySelectedOrganisation);

    //UseLayoutEffect to set IFrame Height on Window Resize
    useLayoutEffect(() => {
        // Function to handle window resize
        const handleResize = () => setIframeHeight(getContentHeight(getWindowInnerHeight()));

        // Add event listener to handle window resize
        window.addEventListener('resize', handleResize);
        handleResize(); // Initialize iframe height

        return () => {
            // Remove event listener on component unmount
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    //Function to get Theme, Locale and Access Token
    const getTheme = () => localStorage.getItem('COLOR_THEME') || '0';
    const getLocale = () => i18next.language || 'en-US';

    const getAccessToken = () => JSON.parse(sessionStorage.getItem(`oidc.user:${getClientID()}`) || '{}')?.access_token || '';

    const getTokenExpiry = () => Number(JSON.parse(sessionStorage.getItem(`oidc.user:${getClientID()}`) || '{}')?.expires_at || 0);

    const isTokenValid = () => {
        const token = getAccessToken();
        const expiresAt = getTokenExpiry();
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const expiryBufferInSeconds = 30;
        return !!token && expiresAt > nowInSeconds + expiryBufferInSeconds;
    };

    const getValidAccessToken = async () => {
        if (isTokenValid()) {
            return getAccessToken();
        }

        try {
            const renewedUser = await authService.renewToken();
            if (renewedUser?.access_token) {
                return renewedUser.access_token;
            }
        } catch (error) {
            console.error('GatewayManagement: token renew failed', error);
        }

        try {
            await authService.login();
        } catch (error) {
            console.error('GatewayManagement: login redirect failed', error);
        }

        return '';
    };

    //Function to get IFrame Element
    const getIframeElement = () => document.getElementById('gateway-management-iframe-id') as HTMLIFrameElement;

    //UseEffect to set IFrame Loaded State
    useEffect(() => {
        //Add Message Listener to get App Context from Gateway Management
        const event = addMessageListener(GATEWAY_MANAGEMENT_HOST_URL, (message) => {
            //Check if message type is appContext
            if (message?.type === 'appContext') {
                //Parse received value from IFrame
                const receivedVal = JSON.parse(message?.payload?.viewType || '{}');
                console.log('GatewayManagement: received value from iot devices: ', receivedVal);

                //Check if gateway Management are loaded
                if (receivedVal?.iotLoaded) {
                    setIsIFrameLoaded(true);
                }
            }
        });

        //Return Cleanup Function
        return () => {
            //Check if Event Listener is available
            if (event) {
                //Dispose Event Listener
                event.dispose();
            }
        };
    }, []);

    useEffect(() => {
        const sendAppContext = async () => {
            //Check if IFrame is loaded
            if (!isIFrameLoaded) {
                return;
            }

            //Get IFrame Content Window
            const iframeContentWindow = getIframeElement()?.contentWindow;

            //Check if IFrame Content Window is available
            if (!iframeContentWindow) {
                return;
            }

            const accessToken = await getValidAccessToken();

            if (!accessToken) {
                sendMessage(GATEWAY_MANAGEMENT_HOST_URL, {
                    type: 'appContext',
                    payload: {
                        viewType: JSON.stringify({
                            theme: getTheme(),
                            locale: getLocale(),
                            rbmAccessToken: '',
                            selectedOrg: orgSelected?.id || '',
                        })
                    }
                }, iframeContentWindow);
                return;
            }

            const viewType = {
                theme: getTheme(),
                locale: getLocale(),
                rbmAccessToken: accessToken,
                selectedOrg: orgSelected?.id || ''
            };
            console.log('viewType: GT: ', viewType);

            //Send App Context to IFrame
            sendMessage(GATEWAY_MANAGEMENT_HOST_URL, {
                type: 'appContext',
                payload: {
                    viewType: JSON.stringify(viewType)
                }
            }, iframeContentWindow);
        };

        sendAppContext();
    }, [
        orgSelected,
        isIFrameLoaded,
        i18next.language,
        localStorage.getItem('COLOR_THEME')
    ]);

    return (
        <div>
            {/* IFrame Component */}
            {!isIFrameLoaded && <div className="gateway-loader-cls"><ContentLoader /></div>}
            <div style={{ display: !isIFrameLoaded ? "none" : "block" }}>
                <IframeComponent
                    width="100%"
                    height={`${iframeHeight}px`}
                    id="gateway-management-iframe-id"
                    url={`${GATEWAY_MANAGEMENT_HOST_URL}?name=gatewaymanagement`}
                />
            </div>
        </div>
    );
};

export default GatewayManagement;